import instance from "~/v2/api";
import { looseArray, looseParse } from "~/v2/api/looseParse";
import type { Order } from "~/v2/api/rest/account";
import { orderSchema } from "~/v2/api/rest/account.schema";
import {
  type CancelOrdersResponse,
  cancelOrdersResponseSchema,
} from "./api.schema";

/** Concrete OMS / gobroker order statuses (1:1 with the backend enum). */
export const OMS_ORDER_STATUSES = [
  "held",
  "accepted",
  "new",
  "partially_filled",
  "filled",
  "done_for_day",
  "canceled",
  "replaced",
  "pending_cancel",
  "stopped",
  "rejected",
  "suspended",
  "pending_new",
  "calculated",
  "expired",
  "accepted_for_bidding",
  "pending_replace",
  "pending_review",
] as const;

export type OmsOrderStatus = (typeof OMS_ORDER_STATUSES)[number];
export type AtdOrderStatus = OmsOrderStatus | "open" | "closed" | "all";
export type AtdOrderSide = "buy" | "sell" | "sell_short";
export type AtdOrderAssetClass = "us_equity" | "us_option" | "crypto";
export type AtdOrderType =
  | "market"
  | "limit"
  | "stop"
  | "stop_limit"
  | "trailing_stop";
export type AtdOrderDirection = "asc" | "desc";

interface GetAtdOrdersBaseParams {
  accountID: string;
  product: "live" | "paper";
  symbols?: string[];
  status?: string;
  side?: AtdOrderSide;
  limit?: number;
  after?: string;
  until?: string;
  assetClass?: AtdOrderAssetClass;
  orderType?: AtdOrderType;
  direction?: AtdOrderDirection;
}

type AtdOrderCursor =
  | {
      /** Cursor for traversing forward through ascending results. */
      afterOrderID?: string;
      beforeOrderID?: never;
    }
  | {
      afterOrderID?: never;
      /** Use the final order ID here to fetch the next descending page. */
      beforeOrderID?: string;
    };

export type GetAtdOrdersParams = GetAtdOrdersBaseParams & AtdOrderCursor;

export interface GetAtdOrderParams {
  accountID: string;
  product: "live" | "paper";
  orderID: string;
}

export interface ReplaceAtdOrderPayload {
  qty?: string;
  limit_price?: string;
  stop_price?: string;
  time_in_force?: string;
}

export interface ReplaceAtdOrderParams extends GetAtdOrderParams {
  orderDetails: ReplaceAtdOrderPayload;
}

type QueryValue = string | number;

const addQueryValue = (
  params: Record<string, QueryValue>,
  key: string,
  value: QueryValue | undefined,
) => {
  if (value !== undefined && value !== "") {
    params[key] = value;
  }
};

export const getAtdOrders = async ({
  accountID,
  product,
  symbols,
  status,
  side,
  limit,
  after,
  until,
  assetClass,
  orderType,
  direction,
  afterOrderID,
  beforeOrderID,
}: GetAtdOrdersParams): Promise<Order[]> => {
  const params: Record<string, QueryValue> = {};
  const symbolList = symbols?.filter(Boolean).join(",");

  addQueryValue(params, "symbols", symbolList);
  addQueryValue(params, "status", status);
  addQueryValue(params, "side", side);
  addQueryValue(params, "limit", limit);
  addQueryValue(params, "after", after);
  addQueryValue(params, "until", until);
  addQueryValue(params, "asset_class", assetClass);
  addQueryValue(params, "order_type", orderType);
  addQueryValue(params, "direction", direction);
  addQueryValue(params, "after_order_id", afterOrderID);
  addQueryValue(params, "before_order_id", beforeOrderID);

  const accountType = product === "live" ? "accounts" : "paper_accounts";
  const { data } = await instance.get(`/${accountType}/${accountID}/orders`, {
    params,
  });

  return looseParse(
    looseArray(orderSchema, "getAtdOrders"),
    data ?? [],
    "getAtdOrders",
  );
};

export const getAtdOrder = async ({
  accountID,
  product,
  orderID,
}: GetAtdOrderParams): Promise<Order | null> => {
  const accountType = product === "live" ? "accounts" : "paper_accounts";
  const { data } = await instance.get(
    `/${accountType}/${accountID}/orders/${orderID}`,
  );

  return data == null ? null : looseParse(orderSchema, data, "getAtdOrder");
};

export const replaceAtdOrder = async ({
  accountID,
  product,
  orderID,
  orderDetails,
}: ReplaceAtdOrderParams): Promise<Order | null> => {
  const accountType = product === "live" ? "accounts" : "paper_accounts";
  const { data } = await instance.patch(
    `/${accountType}/${accountID}/orders/${orderID}`,
    orderDetails,
  );

  return data == null ? null : looseParse(orderSchema, data, "replaceAtdOrder");
};

export interface CancelOrdersParams {
  accountID: string;
  product: "live" | "paper";
  orderIDs: string[];
}

export interface CancelOrderResult {
  id: string;
  ok: boolean;
  error?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Defensive: `looseParse` may hand back the raw payload on failure. */
const normalizeLiveCancelResults = (
  response: CancelOrdersResponse | null | undefined,
  requestedIds: string[],
): CancelOrderResult[] => {
  const requestedIdSet = new Set(requestedIds);
  const statuses = Array.isArray(response?.statuses) ? response.statuses : [];
  const byId = new Map<string, CancelOrderResult>();
  const duplicateIds = new Set<string>();

  for (const status of statuses) {
    if (!isRecord(status)) {
      continue;
    }

    const id = typeof status.id === "string" ? status.id : "";
    if (!id || !requestedIdSet.has(id)) {
      continue;
    }
    if (byId.has(id)) {
      duplicateIds.add(id);
      continue;
    }

    const error =
      typeof status.error === "string" && status.error
        ? status.error
        : undefined;
    byId.set(id, {
      id,
      ok: status.ok === true,
      error:
        typeof status.ok === "boolean"
          ? error
          : (error ?? "Invalid cancel status returned"),
    });
  }

  return requestedIds.map((id) => {
    if (duplicateIds.has(id)) {
      return {
        id,
        ok: false,
        error: "Duplicate cancel statuses returned",
      };
    }

    return (
      byId.get(id) ?? {
        id,
        ok: false,
        error: "No cancel status returned",
      }
    );
  });
};

const cancelOrderById = async ({
  accountID,
  product,
  orderID,
}: {
  accountID: string;
  product: "live" | "paper";
  orderID: string;
}): Promise<void> => {
  const accountType = product === "live" ? "accounts" : "paper_accounts";
  await instance.delete(`/${accountType}/${accountID}/orders/${orderID}`);
};

/**
 * Live accounts use trading-api → OMS CancelOrders. Paper falls back to
 * parallel single deletes until a paper OMS path exists.
 */
export const cancelOrders = async ({
  accountID,
  product,
  orderIDs,
}: CancelOrdersParams): Promise<CancelOrderResult[]> => {
  const ids = orderIDs.filter(Boolean);
  if (ids.length === 0) {
    return [];
  }

  if (product === "live") {
    const { data } = await instance.post(
      `/accounts/${accountID}/orders/cancel`,
      { order_ids: ids },
    );
    const parsed = looseParse(cancelOrdersResponseSchema, data, "cancelOrders");
    return normalizeLiveCancelResults(parsed, ids);
  }

  const settled = await Promise.allSettled(
    ids.map((orderID) => cancelOrderById({ accountID, product, orderID })),
  );
  return settled.map((result, index) => {
    const id = ids[index] ?? "";
    if (result.status === "fulfilled") {
      return { id, ok: true } satisfies CancelOrderResult;
    }
    const reason = result.reason;
    return {
      id,
      ok: false,
      error: reason instanceof Error ? reason.message : "Cancel failed",
    } satisfies CancelOrderResult;
  });
};
